import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
} from '@mui/material';
import { PrimaryButton } from '@components';
import { triggerBlobDownload } from '../../../networkRequest/api';

const CHUNK_SIZE = 100;

// Appends `_${index}` to the filename before the extension
const filenameWithIndex = (filename: string, index: number): string => {
  const dotIndex = filename.lastIndexOf('.');
  return dotIndex !== -1
    ? `${filename.slice(0, dotIndex)}_${index}${filename.slice(dotIndex)}`
    : `${filename}_${index}`;
};

type ExportPdfDialogProps = {
  open: boolean;
  onClose: () => void;
  totalCount: number;
  fetchPdfReportChunk: (offset: number, limit: number, pageOffset: number) => Promise<{ blob: Blob; filename: string; headers: any }>;
};

const ExportPdfDialog: React.FC<ExportPdfDialogProps> = ({ open, onClose, totalCount, fetchPdfReportChunk }) => {
  const [done, setDone] = useState(false);
  const [error, setError] = useState(false);
  const cancelledRef = useRef(false);
  const [totalChunks, setTotalChunks] = useState<number>(1);
  const [chunk, setChunk] = useState(0);

  const progress = (chunk / totalChunks) * 100;

  useEffect(() => {
    if (!open) return;

    cancelledRef.current = false;
    setDone(false);
    setError(false);

    const _totalChunks = Math.ceil(totalCount / CHUNK_SIZE) || 1;
    setChunk(0);
    setTotalChunks(_totalChunks);

    const run = async () => {
      try {
        let baseFilename: string | null = null;
        let reportPageOffset = 0;
        for (let _chunk = 0; _chunk < _totalChunks; _chunk++) {
          if (cancelledRef.current) return;
          const { blob, filename, headers } = await fetchPdfReportChunk(
            _chunk * CHUNK_SIZE, CHUNK_SIZE, reportPageOffset
          );
          reportPageOffset = parseInt(headers['x-surepass-report-page-offset'], 10) || 0;
          if (cancelledRef.current) return;

          if (baseFilename === null) {
            baseFilename = filename;
          }
          triggerBlobDownload(blob, filenameWithIndex(baseFilename, _chunk + 1));
          setChunk(_chunk + 1);
        }
        if (!cancelledRef.current) {
          setDone(true);
        }
      } catch {
        if (!cancelledRef.current) {
          setError(true);
        }
      }
    };

    run();

    return () => { cancelledRef.current = true; };
  }, [fetchPdfReportChunk, open, totalCount]);

  const plural = totalChunks > 1;
  const statusText = done
    ? `Your ${plural ? 'PDFs were' : 'PDF was'} generated successfully.`
    : `Your ${plural ? 'PDFs are' : 'PDF is'} being generated.`;

  const handleCancel = () => {
    cancelledRef.current = true;
    onClose();
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth>
      <DialogTitle>Export PDF</DialogTitle>
      <DialogContent>
        {error ? (
          <Typography color="error">
            Something went wrong. Please check your connection and try again.
          </Typography>
        ) : (
          <>
            <Typography>{statusText}</Typography>
            <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
              <LinearProgress
                sx={{ flex: 1 }}
                variant={(totalChunks === 1 && !done) ? 'indeterminate' : 'determinate'}
                value={progress}
              />
              { totalChunks > 1 && (
                <Typography>{chunk} of {totalChunks}</Typography>
              )}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3 }}>
        <PrimaryButton
          sx={{ textTransform: 'none' }}
          action={handleCancel}
        >
          {done ? 'Close' : 'Cancel'}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};

export default ExportPdfDialog;
